import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Alert,
} from '@mui/material'
import Tree from '../tree/Tree'
import useVariableTree from '../../hooks/useVariableTree'
import { GenNode } from '../../types/carbon/models/GenNode'
import { getNodeID } from '../../utils/nodeTypes'

interface FolderBrowserModalProps {
  open: boolean
  customer: string
  job: string
  currentPath?: string
  onConfirm: (path: string) => void
  onCancel: () => void
}

const FolderBrowserModal: React.FC<FolderBrowserModalProps> = ({
  open,
  customer,
  job,
  currentPath,
  onConfirm,
  onCancel,
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(undefined)
  const [childFolderName, setChildFolderName] = useState('')
  const [childFolderDialogOpen, setChildFolderDialogOpen] = useState(false)
  const [childFolderError, setChildFolderError] = useState<string | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)

  const variableTree = useVariableTree({
    customer,
    job,
    loadCodes: false,
    nodeFilter: (node: GenNode) => node.type === 'Folder',
    autoExpandVariables: false,
  })

  const handleFolderSelect = (itemId: string, node: GenNode, isSelected: boolean) => {
    if (isSelected) {
      setSelectedFolderId(getNodeID(node))
      // Clear child folder when selecting a different folder
      setChildFolderName('')
      setChildFolderError(null)
      setMenuAnchorEl(null) // Close menu when selecting a different folder
    } else {
      setSelectedFolderId(undefined)
      setMenuAnchorEl(null)
    }
  }

  const handleCreateChildFolder = () => {
    setChildFolderDialogOpen(true)
  }


  const handleChildFolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setChildFolderName(value)

    // Validate folder name
    if (value) {
      if (value.includes('/') || value.includes('\\')) {
        setChildFolderError('Folder name cannot contain slashes')
      } else if (/[<>:"|?*]/.test(value)) {
        setChildFolderError('Folder name cannot contain: < > : " | ? *')
      } else if (value.startsWith(' ') || value.endsWith(' ')) {
        setChildFolderError('Folder name cannot start or end with spaces')
      } else {
        setChildFolderError(null)
      }
    } else {
      setChildFolderError(null)
    }
  }

  const handleChildFolderCreate = () => {
    if (childFolderName && !childFolderError) {
      setChildFolderDialogOpen(false)
      handleConfirm()
    }
  }

  const handleMenuClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation()
    }
    setMenuAnchorEl(null)
  }

  const handleCreateChildFolderClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    handleMenuClose()
    handleCreateChildFolder()
  }

  const getSelectedFolderPath = (): string => {
    if (selectedFolderId) {
      const selectedNode = variableTree.nodeMap[selectedFolderId]
      if (selectedNode) {
        // Construct full path from value2 (parent path) and value1 (folder name)
        const fullPath = selectedNode.value2 ? `${selectedNode.value2}/${selectedNode.value1}` : selectedNode.value1 || ''
        return fullPath
      }
    }
    return ''
  }

  const getFullPath = (): string => {
    const basePath = getSelectedFolderPath()
    if (childFolderName && basePath) {
      return `${basePath}/${childFolderName}`
    }
    return basePath
  }

  const handleConfirm = () => {
    const fullPath = getFullPath()
    if (fullPath) {
      onConfirm(fullPath)
    }
  }

  const handleCancel = () => {
    setSelectedFolderId(undefined)
    setChildFolderName('')
    setChildFolderError(null)
    setMenuAnchorEl(null)
    onCancel()
  }

  
  const handleChildFolderDialogClose = () => {
    setChildFolderDialogOpen(false)
    setChildFolderError(null)
    setSelectedFolderId(undefined)  
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
          }
        }}
      >
        <DialogTitle>
          Select Destination Folder
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an existing folder from the variable tree below.
          </Typography>

          {variableTree.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {variableTree.error instanceof Error ? variableTree.error.message : 'Failed to load folders'}
            </Alert>
          )}

          <Box sx={{ flex: 1, minHeight: 0, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Tree
              nodes={variableTree.nodes}
              expanded={variableTree.expanded}
              selected={selectedFolderId}
              showTooltips={false}
              disabled={variableTree.isLoading}
              onNodeExpandToggle={variableTree.onNodeExpandToggle}
              onNodeSelectToggle={handleFolderSelect}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>
          {/* data-voice="confirm": Douglas asks before pressing (#1363) — writes a new folder to Azure blob storage */}
          <Button
            onClick={handleCreateChildFolderClick}
            variant="outlined"
            color="info"
            disabled={selectedFolderId ? false : true}
            data-voice="confirm"
          >
            Create New Folder
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="info"
            disabled={selectedFolderId ? false : true}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      {/* Child Folder Creation Dialog */}
      <Dialog
        open={childFolderDialogOpen}
        onClose={handleChildFolderDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Child Folder</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Creating folder in: <strong>{getSelectedFolderPath()}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Folder Name"
              value={childFolderName}
              onChange={handleChildFolderNameChange}
              error={!!childFolderError}
              helperText={childFolderError || 'Enter a name for the new folder (no slashes)'}
              autoFocus
              sx={{ mb: 2 }}
            />
            {childFolderName && !childFolderError && (
              <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" color="info.contrastText">
                  <strong>New path will be:</strong> {getSelectedFolderPath()}/{childFolderName}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChildFolderDialogClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleChildFolderCreate}
            variant="contained"
            color="info"
            disabled={!childFolderName || !!childFolderError}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default FolderBrowserModal
